/**
 * Platform profiles for CSV roster import.
 * Each profile defines column mappings, row filters, and date transforms
 * for a specific sports management platform's export format.
 */

const PLATFORM_PROFILES = {
  generic: {
    id: "generic",
    label: "Generic CSV",
    description:
      "Standard CSV with First_Name, Last_Name, Number, Position, etc.",
    columnMap: {
      first_name: [
        "first_name",
        "firstname",
        "first name",
        "first",
        "player first name",
      ],
      last_name: [
        "last_name",
        "lastname",
        "last name",
        "last",
        "player last name",
      ],
      name: ["name", "full name", "player name", "player"],
      number: [
        "number",
        "jersey number",
        "jersey",
        "#",
        "jersey #",
        "jersey_number",
        "uniform number",
      ],
      position: ["position", "pos"],
      grade: ["grade", "grade level"],
      grad_year: [
        "grad_year",
        "gradyear",
        "grad year",
        "graduation year",
        "graduation_year",
        "class of",
      ],
      dob: [
        "dob",
        "date_of_birth",
        "date of birth",
        "birthdate",
        "birthday",
        "birth date",
      ],
      email: [
        "email",
        "email address",
        "player email",
        "email address 1",
      ],
      parent_emails: [
        "parent email",
        "guardian email",
        "parent email 1",
        "parent email address",
        "parent/guardian email",
      ],
      notes: ["notes", "note", "comments"],
      group: [
        "group",
        "team",
        "team name",
        "division",
        "division name",
        "program name",
        "age level",
      ],
    },
    rowFilter: null,
    dateTransform: null,
  },

  teamsnap: {
    id: "teamsnap",
    label: "TeamSnap",
    description:
      "Import from a TeamSnap team-level or organization-level roster export.",
    columnMap: {
      first_name: ["first name", "first"],
      last_name: ["last name", "last"],
      name: ["name", "player name"],
      number: ["jersey number", "number", "jersey"],
      position: ["position", "pos"],
      grade: [],
      grad_year: [],
      dob: ["birthday", "birthdate", "date of birth"],
      email: ["email address 1", "email address", "email"],
      parent_emails: ["parent email", "guardian email"],
      notes: ["notes"],
      group: ["team", "division"],
    },
    rowFilter: (rowValues, headerIndex) => {
      // Filter out non-players: check "non-player (y/n)" column
      const nonPlayerIdx =
        headerIndex["non-player (y/n)"] ?? headerIndex["non-player"];
      if (nonPlayerIdx !== undefined) {
        const val = (rowValues[nonPlayerIdx] || "").trim().toLowerCase();
        return val !== "y" && val !== "yes";
      }
      return true;
    },
    dateTransform: null,
  },

  sportsengine: {
    id: "sportsengine",
    label: "SportsEngine",
    description:
      "Import from a SportsEngine HQ Teams export or Tourney roster.",
    columnMap: {
      first_name: ["first name", "first_name", "first"],
      last_name: ["last name", "last_name", "last"],
      name: ["name", "player name"],
      number: ["jersey number", "jersey_number", "number", "jersey"],
      position: ["position", "pos"],
      grade: ["grade"],
      grad_year: ["graduation year", "graduation_year", "grad year"],
      dob: ["date of birth", "birthdate", "date_of_birth"],
      email: ["email"],
      parent_emails: ["parent email", "guardian email"],
      notes: ["notes"],
      group: ["team name", "team", "division name", "division"],
    },
    rowFilter: (rowValues, headerIndex) => {
      // Filter out Staff: check "team role" column
      const roleIdx = headerIndex["team role"];
      if (roleIdx !== undefined) {
        const val = (rowValues[roleIdx] || "").trim().toLowerCase();
        return val !== "staff" && val !== "coach" && val !== "manager";
      }
      return true;
    },
    dateTransform: (dateStr) => {
      // SportsEngine uses YYYY/MM/DD
      if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateStr)) {
        return dateStr.replace(/\//g, "-");
      }
      return dateStr;
    },
  },

  crossbar: {
    id: "crossbar",
    label: "Crossbar",
    description: "Import from a Crossbar roster or member export.",
    columnMap: {
      first_name: [
        "player first name",
        "first name",
        "first_name",
        "first",
        "athlete first name",
      ],
      last_name: [
        "player last name",
        "last name",
        "last_name",
        "last",
        "athlete last name",
      ],
      name: ["player name", "name", "athlete name"],
      number: ["jersey number", "number", "jersey"],
      position: ["position", "pos"],
      grade: ["grade"],
      grad_year: ["graduation year"],
      dob: ["birthdate", "birthday", "date of birth", "dob", "player birthdate"],
      email: ["email", "email address", "player email"],
      parent_emails: ["parent email", "guardian email", "parent/guardian email"],
      notes: ["notes"],
      group: ["team", "age level", "age group", "division"],
    },
    rowFilter: null,
    dateTransform: null,
  },

  leagueapps: {
    id: "leagueapps",
    label: "LeagueApps",
    description:
      "Import from a LeagueApps registration or member export. Columns vary by organization.",
    columnMap: {
      first_name: ["first name", "first_name", "firstname", "first"],
      last_name: ["last name", "last_name", "lastname", "last"],
      name: ["name", "player name", "full name"],
      number: [
        "jersey number",
        "number",
        "jersey",
        "uniform number",
        "jersey #",
      ],
      position: ["position", "pos"],
      grade: ["grade", "grade level"],
      grad_year: [
        "graduation year",
        "grad year",
        "grad_year",
        "class of",
      ],
      dob: ["birthdate", "date of birth", "birthday", "dob"],
      email: [
        "email",
        "email address",
        "player email",
      ],
      parent_emails: [
        "parent email",
        "guardian email",
        "parent/guardian email",
      ],
      notes: ["notes", "comments"],
      group: [
        "team name",
        "team",
        "program name",
        "program",
        "division",
        "division name",
        "season name",
      ],
    },
    rowFilter: null,
    dateTransform: null,
  },
};

/**
 * Get all platform profiles as an array.
 */
export function getAllProfiles() {
  return Object.values(PLATFORM_PROFILES);
}

/**
 * Get a platform profile by ID, falling back to generic.
 */
export function getProfileById(id) {
  return PLATFORM_PROFILES[id] || PLATFORM_PROFILES.generic;
}

/**
 * Resolve column mapping for a set of CSV headers against a platform profile.
 * Uses exact match (not substring) and tracks used indices to prevent double-mapping.
 *
 * @param {string[]} headers - Lowercase CSV header strings
 * @param {object} profile - Platform profile
 * @returns {object} Map of { field: { headerIndex, matchedHeader } | null }
 */
export function resolveColumnMapping(headers, profile) {
  const mapping = {};
  const usedIndices = new Set();

  for (const [field, candidates] of Object.entries(profile.columnMap)) {
    mapping[field] = null;
    for (const candidate of candidates) {
      const index = headers.indexOf(candidate);
      if (index >= 0 && !usedIndices.has(index)) {
        usedIndices.add(index);
        mapping[field] = { headerIndex: index, matchedHeader: headers[index] };
        break;
      }
    }
  }

  return mapping;
}

/**
 * Auto-detect which platform a CSV export came from based on its headers.
 * Scores each profile by counting unique header matches + bonus for
 * platform-specific indicator columns.
 *
 * @param {string[]} headers - Lowercase CSV header strings
 * @returns {string} Platform ID (e.g., "teamsnap", "sportsengine", "generic")
 */
export function detectPlatform(headers) {
  const profiles = getAllProfiles().filter((p) => p.id !== "generic");
  let bestMatch = { id: "generic", score: 0 };

  for (const profile of profiles) {
    // Collect all unique candidate headers for this profile
    const allCandidates = new Set();
    for (const candidates of Object.values(profile.columnMap)) {
      candidates.forEach((c) => allCandidates.add(c));
    }

    // Count matches
    let score = 0;
    for (const header of headers) {
      if (allCandidates.has(header)) score++;
    }

    // Bonus for platform-specific indicator columns
    if (
      profile.id === "teamsnap" &&
      headers.some(
        (h) => h === "non-player (y/n)" || h.includes("email address 1 label"),
      )
    ) {
      score += 3;
    }
    if (profile.id === "sportsengine" && headers.includes("team role")) {
      score += 3;
    }
    if (
      profile.id === "crossbar" &&
      headers.some(
        (h) => h.includes("player first name") || h.includes("age level"),
      )
    ) {
      score += 3;
    }
    if (
      profile.id === "leagueapps" &&
      headers.some(
        (h) => h === "program name" || h === "registration id" || h === "season name",
      )
    ) {
      score += 3;
    }

    if (score > bestMatch.score) {
      bestMatch = { id: profile.id, score };
    }
  }

  return bestMatch.score >= 3 ? bestMatch.id : "generic";
}
